import type { WorkExperienceCompany, WorkExperienceRole } from '@applicator/shared'

import { PDF_CONFIG } from './resumeExportPdfConfig'
import {
  isFirstWorkExperienceRoleInCompanyPreview,
  isWorkExperienceCompanyHeaderInRolePreview,
  isWorkExperienceRoleInPreview,
} from './workExperiencePreview'

const BULLET = ' • '

/** Teal: entire company line is bold; company name one step up from location/dates. */
export function workExperienceTealLine1FontPt(kind: WorkExperienceTealLine1Part['kind']): number {
  return kind === 'company'
    ? PDF_CONFIG.fontSize.companyLineCompany
    : PDF_CONFIG.fontSize.companyLineMeta
}

/** Teal-style date range on the company/location line (not tied to position toggle). */
export function formatWorkExperienceRoleDateRange(role: WorkExperienceRole): string {
  if (!role.startDate && !role.endDate) return ''
  const end = role.endDate === 'Present' ? 'Present' : role.endDate || 'Present'
  const start = role.startDate || ''
  if (!start && !end) return ''
  return `${start} - ${end}`.trim()
}

/** MM/YYYY → sortable month index; null for anything else (e.g. Present, empty). */
function dateMonthKey(raw: string | undefined): number | null {
  const m = /^(\d{1,2})\/(\d{4})$/.exec((raw || '').trim())
  if (!m) return null
  return Number(m[2]) * 12 + Number(m[1])
}

/**
 * Teal company line dates: span across visible roles
 * (earliest start — latest end; any open role makes the end "Present").
 */
export function formatWorkExperienceCompanyDateRange(company: WorkExperienceCompany): string {
  const roles = (company.roles || []).filter(
    (r) => isWorkExperienceRoleInPreview(company, r) && (r.startDate || r.endDate)
  )
  if (roles.length === 0) return ''
  if (roles.length === 1) return formatWorkExperienceRoleDateRange(roles[0]!)

  let start = ''
  let startKey = Number.POSITIVE_INFINITY
  let end = ''
  let endKey = Number.NEGATIVE_INFINITY
  for (const r of roles) {
    const sk = dateMonthKey(r.startDate)
    if (sk !== null && sk < startKey) {
      startKey = sk
      start = r.startDate!.trim()
    }
    const endRaw = (r.endDate || 'Present').trim()
    const isPresent = /^present$/i.test(endRaw)
    const ek = isPresent ? Number.MAX_SAFE_INTEGER : dateMonthKey(endRaw)
    if (ek !== null && ek > endKey) {
      endKey = ek
      end = isPresent ? 'Present' : endRaw
    }
  }
  if (!start && !end) return ''
  return `${start} - ${end}`.trim()
}

export type WorkExperienceTealLine1Part =
  | { kind: 'company'; text: string }
  | { kind: 'text'; text: string }

/**
 * Inline segments for line 1: Company • Location • Dates (Teal alignments: left/left).
 * Teal shows this line once per company (on the first visible role) with the
 * company-wide date span; later roles render no company line.
 */
export function workExperienceTealLine1Parts(
  company: WorkExperienceCompany,
  role: WorkExperienceRole
): WorkExperienceTealLine1Part[] {
  if (!isWorkExperienceRoleInPreview(company, role)) return []
  if (!isFirstWorkExperienceRoleInCompanyPreview(company, role)) return []

  const parts: WorkExperienceTealLine1Part[] = []

  const showCompanyName =
    isWorkExperienceCompanyHeaderInRolePreview(company, role) &&
    (company.nameIncluded ?? true) &&
    Boolean(company.name?.trim())

  if (showCompanyName) {
    parts.push({ kind: 'company', text: company.name!.trim() })
  }

  if ((role.locationIncluded ?? true) && role.location?.trim()) {
    parts.push({ kind: 'text', text: role.location.trim() })
  }

  const dates = formatWorkExperienceCompanyDateRange(company)
  if ((role.locationIncluded ?? true) && dates) {
    parts.push({ kind: 'text', text: dates })
  }

  return parts
}

export function workExperienceTealLine1HasContent(parts: WorkExperienceTealLine1Part[]): boolean {
  return parts.length > 0
}

export type WorkExperienceTealLine2Part =
  | { kind: 'position'; text: string }
  | { kind: 'text'; text: string }

/** Teal line 2: Position • Employment type • Dates (inline, like company line). */
export function workExperienceTealLine2Parts(role: WorkExperienceRole): WorkExperienceTealLine2Part[] {
  const parts: WorkExperienceTealLine2Part[] = []

  if ((role.positionIncluded ?? true) && role.position?.trim()) {
    parts.push({ kind: 'position', text: role.position.trim() })
  }

  if (role.employmentTypeIncluded && role.employmentType?.trim()) {
    parts.push({ kind: 'text', text: role.employmentType.trim() })
  }

  const dates = formatWorkExperienceRoleDateRange(role)
  if ((role.locationIncluded ?? true) && dates) {
    parts.push({ kind: 'text', text: dates })
  }

  return parts
}

export function workExperienceTealLine2HasContent(parts: WorkExperienceTealLine2Part[]): boolean {
  return parts.length > 0
}

/** Single flowing line for PDF (Position • Employment • Dates). */
export function workExperienceTealLine2PlainText(parts: WorkExperienceTealLine2Part[]): string {
  return parts.map((p) => p.text).join(BULLET)
}

/** Single flowing line for PDF/DOCX (Company • Location • Dates). */
export function workExperienceTealLine1PlainText(parts: WorkExperienceTealLine1Part[]): string {
  return parts.map((p) => p.text).join(BULLET)
}

export { BULLET as WORK_EXPERIENCE_TEAL_HEADER_BULLET }
